import { useState, useMemo, useEffect, useCallback } from "react";

const BASE = "https://support.gs-group.fr/api";
const hdr  = () => ({ Authorization:"Bearer "+localStorage.getItem("nexo_token"), "Content-Type":"application/json" });

const CATS_STOCK = [
  "Caméra IP","Caméra HDCVI","NVR (IP)","XVR (HDCVI)","Alarme AJAX",
  "Contrôle d'accès","Alimentation","Coffret / Armoire","Switch / Réseau",
  "Câblage","Détection incendie","Accessoire","Piles / Batteries","Logiciel","Divers"
];

// ── Helpers ──────────────────────────────────────────────────────
function fmtEur(v) { return v!=null ? Number(v).toFixed(2).replace(".",",")+" €" : "—"; }
function fmtDays(d) { return d ? `${d}j` : "—"; }

function QtyBadge({ qty }) {
  if (qty === 0) return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"4px 11px", borderRadius:20, background:"#fef2f2", border:"1px solid #fecaca", color:"#dc2626", fontSize:11, fontWeight:700 }}>
      <span style={{ width:6, height:6, borderRadius:"50%", background:"#ef4444", flexShrink:0 }}/>Rupture
    </span>
  );
  if (qty <= 3) return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"4px 11px", borderRadius:20, background:"#fffbeb", border:"1px solid #fde68a", color:"#b45309", fontSize:11, fontWeight:700 }}>
      <span style={{ width:6, height:6, borderRadius:"50%", background:"#f59e0b", flexShrink:0 }}/>{qty} · Critique
    </span>
  );
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"4px 11px", borderRadius:20, background:"#f0fdf4", border:"1px solid #bbf7d0", color:"#15803d", fontSize:11, fontWeight:700 }}>
      <span style={{ width:6, height:6, borderRadius:"50%", background:"#22c55e", flexShrink:0 }}/>{qty} · OK
    </span>
  );
}

// ── Modal comparateur de prix ─────────────────────────────────────
function PrixModal({ T, produit, onClose }) {
  const [lignes, setLignes]         = useState([]);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [form, setForm]             = useState({ fournisseur_id:"", prix_unitaire:"", reference_fournisseur:"", delai_livraison_jours:"" });
  const [saving, setSaving]         = useState(false);
  const [showAdd, setShowAdd]       = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [l, f] = await Promise.all([
      fetch(`${BASE}/fournisseurs/produit/${produit.id}`, { headers:hdr() }).then(r=>r.json()).catch(()=>[]),
      fetch(`${BASE}/fournisseurs`, { headers:hdr() }).then(r=>r.json()).catch(()=>[]),
    ]);
    setLignes(Array.isArray(l) ? l : []);
    setFournisseurs(Array.isArray(f) ? f : []);
    setLoading(false);
  }, [produit.id]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.fournisseur_id) return;
    setSaving(true);
    try {
      const r = await fetch(`${BASE}/fournisseurs/produit/${produit.id}`, {
        method:"POST", headers:hdr(),
        body:JSON.stringify({
          fournisseur_id: Number(form.fournisseur_id),
          prix_unitaire: form.prix_unitaire ? parseFloat(form.prix_unitaire.replace(",",".")) : null,
          reference_fournisseur: form.reference_fournisseur || null,
          delai_livraison_jours: form.delai_livraison_jours ? parseInt(form.delai_livraison_jours) : null,
        }),
      });
      if (!r.ok) { const e = await r.json().catch(()=>({error:`HTTP ${r.status}`})); alert(e.error||"Erreur serveur"); setSaving(false); return; }
      setForm({ fournisseur_id:"", prix_unitaire:"", reference_fournisseur:"", delai_livraison_jours:"" });
      setShowAdd(false);
      load();
    } catch(e) { alert("Erreur réseau : " + e.message); }
    setSaving(false);
  };

  const remove = async (fId) => {
    if (!window.confirm("Retirer ce fournisseur ?")) return;
    await fetch(`${BASE}/fournisseurs/produit/${produit.id}/${fId}`, { method:"DELETE", headers:hdr() });
    load();
  };

  const minPrix = lignes.length ? Math.min(...lignes.map(l=>parseFloat(l.prix_unitaire)||Infinity)) : null;
  const available = fournisseurs.filter(f => !lignes.some(l => l.fournisseur_id === f.id));

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(8,12,30,.4)", backdropFilter:"blur(6px)", zIndex:2000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:20, width:"min(680px,100%)", maxHeight:"90vh", overflowY:"auto", boxShadow:"0 24px 64px rgba(0,0,0,.25)", animation:"nx-modal-open .28s cubic-bezier(.16,1,.3,1) both" }}>

        {/* Header */}
        <div style={{ padding:"22px 24px 16px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
          <div>
            <div style={{ fontSize:16, fontWeight:900, color:T.text, display:"flex", alignItems:"center", gap:8 }}>
              💰 Comparateur de prix
            </div>
            <div style={{ fontSize:13, color:T.muted, marginTop:3 }}>
              {produit.designation || produit.nom}
              {produit.reference && <span style={{ fontFamily:"monospace", marginLeft:8, fontSize:11, opacity:.7 }}>{produit.reference}</span>}
            </div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:T.muted, cursor:"pointer", fontSize:20, lineHeight:1, padding:4 }}>✕</button>
        </div>

        <div style={{ padding:"18px 24px 24px" }}>

          {loading ? (
            <div style={{ textAlign:"center", padding:30, color:T.muted }}>⏳ Chargement…</div>
          ) : (
            <>
              {/* Tableau comparatif */}
              {lignes.length === 0 ? (
                <div style={{ textAlign:"center", padding:"30px 0", color:T.muted }}>
                  <div style={{ fontSize:36, marginBottom:8 }}>🏭</div>
                  <div style={{ fontSize:13 }}>Aucun fournisseur pour ce produit</div>
                  <div style={{ fontSize:11, marginTop:4, opacity:.7 }}>Ajoutez-en un ci-dessous</div>
                </div>
              ) : (
                <div style={{ marginBottom:20 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:".7px", marginBottom:10 }}>
                    {lignes.length} fournisseur{lignes.length>1?"s":" "}
                  </div>
                  <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                    {lignes.map((l, i) => {
                      const isBest = l.prix_unitaire != null && parseFloat(l.prix_unitaire) === minPrix;
                      return (
                        <div key={l.fournisseur_id} style={{
                          display:"grid", gridTemplateColumns:"1fr auto auto auto auto",
                          alignItems:"center", gap:12, padding:"14px 16px",
                          background: isBest ? "#f0fdf4" : T.bg,
                          border: `1.5px solid ${isBest ? "#86efac" : T.border}`,
                          borderRadius:12, transition:"all .15s"
                        }}>
                          {/* Fournisseur */}
                          <div>
                            <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                              {isBest && (
                                <span style={{ fontSize:10, fontWeight:800, color:"#15803d", background:"#dcfce7", padding:"2px 8px", borderRadius:20, whiteSpace:"nowrap" }}>
                                  🏆 Meilleur prix
                                </span>
                              )}
                              <div style={{ fontSize:13, fontWeight:700, color:T.text }}>{l.fournisseur_nom}</div>
                            </div>
                            {l.reference_fournisseur && (
                              <div style={{ fontSize:11, color:T.muted, fontFamily:"monospace", marginTop:2 }}>
                                Réf. {l.reference_fournisseur}
                              </div>
                            )}
                            {l.fournisseur_tel && (
                              <div style={{ fontSize:11, color:T.muted, marginTop:1 }}>📞 {l.fournisseur_tel}</div>
                            )}
                          </div>

                          {/* Prix */}
                          <div style={{ textAlign:"right" }}>
                            <div style={{ fontSize:20, fontWeight:900, color:isBest?"#15803d":T.text, lineHeight:1 }}>
                              {fmtEur(l.prix_unitaire)}
                            </div>
                            <div style={{ fontSize:10, color:T.muted, marginTop:2 }}>HT / unité</div>
                          </div>

                          {/* Délai */}
                          <div style={{ textAlign:"center", minWidth:50 }}>
                            <div style={{ fontSize:14, fontWeight:700, color:T.text }}>{fmtDays(l.delai_livraison_jours)}</div>
                            <div style={{ fontSize:10, color:T.muted }}>délai</div>
                          </div>

                          {/* Mailto */}
                          {l.fournisseur_email && (
                            <a href={`mailto:${l.fournisseur_email}?subject=Commande - ${produit.designation||produit.nom}&body=Référence fournisseur : ${l.reference_fournisseur||"—"}%0AQuantité souhaitée :`}
                              style={{ padding:"6px 12px", borderRadius:8, background:T.accentDim, border:`1px solid ${T.border}`, color:T.accent, fontSize:11, fontWeight:700, textDecoration:"none", whiteSpace:"nowrap" }}>
                              📧 Commander
                            </a>
                          )}

                          {/* Supprimer */}
                          <button onClick={() => remove(l.fournisseur_id)}
                            style={{ padding:"6px 10px", borderRadius:8, border:"1px solid #fecaca", background:"#fef2f2", color:"#dc2626", fontSize:12, cursor:"pointer" }}>
                            🗑
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Formulaire ajout */}
              {showAdd ? (
                <div style={{ background:T.bg, border:`1px solid ${T.border}`, borderRadius:14, padding:18 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:T.text, marginBottom:14 }}>
                    ＋ Ajouter un fournisseur
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
                    {/* Sélecteur fournisseur */}
                    <div style={{ gridColumn:"1/-1" }}>
                      <label style={{ fontSize:11, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:".6px", display:"block", marginBottom:5 }}>Fournisseur *</label>
                      <select value={form.fournisseur_id} onChange={e=>setForm(p=>({...p,fournisseur_id:e.target.value}))}
                        style={{ width:"100%", background:T.card, border:`1px solid ${T.border}`, borderRadius:9, padding:"10px 12px", color:T.text, fontSize:13, outline:"none", cursor:"pointer" }}>
                        <option value="">— Choisir un fournisseur —</option>
                        {available.map(f=><option key={f.id} value={f.id}>{f.nom}</option>)}
                        {available.length===0 && <option disabled>Tous les fournisseurs sont déjà liés</option>}
                      </select>
                    </div>
                    {/* Prix */}
                    <div>
                      <label style={{ fontSize:11, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:".6px", display:"block", marginBottom:5 }}>Prix HT (€)</label>
                      <input type="text" value={form.prix_unitaire} onChange={e=>setForm(p=>({...p,prix_unitaire:e.target.value}))}
                        placeholder="ex : 24,90"
                        style={{ width:"100%", background:T.card, border:`1px solid ${T.border}`, borderRadius:9, padding:"10px 12px", color:T.text, fontSize:13, outline:"none", boxSizing:"border-box" }}/>
                    </div>
                    {/* Délai */}
                    <div>
                      <label style={{ fontSize:11, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:".6px", display:"block", marginBottom:5 }}>Délai (jours)</label>
                      <input type="number" min="0" value={form.delai_livraison_jours} onChange={e=>setForm(p=>({...p,delai_livraison_jours:e.target.value}))}
                        placeholder="ex : 3"
                        style={{ width:"100%", background:T.card, border:`1px solid ${T.border}`, borderRadius:9, padding:"10px 12px", color:T.text, fontSize:13, outline:"none", boxSizing:"border-box" }}/>
                    </div>
                    {/* Réf fournisseur */}
                    <div style={{ gridColumn:"1/-1" }}>
                      <label style={{ fontSize:11, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:".6px", display:"block", marginBottom:5 }}>Référence fournisseur</label>
                      <input type="text" value={form.reference_fournisseur} onChange={e=>setForm(p=>({...p,reference_fournisseur:e.target.value}))}
                        placeholder="ex : AJX-MOTION-PIR"
                        style={{ width:"100%", background:T.card, border:`1px solid ${T.border}`, borderRadius:9, padding:"10px 12px", color:T.text, fontSize:13, outline:"none", boxSizing:"border-box" }}/>
                    </div>
                  </div>
                  <div style={{ display:"flex", gap:9 }}>
                    <button onClick={save} disabled={saving||!form.fournisseur_id}
                      style={{ flex:1, padding:"10px", borderRadius:10, background:`linear-gradient(135deg,${T.accent},${T.purple||"#8b5cf6"})`, border:"none", color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer", opacity:!form.fournisseur_id?.5:1 }}>
                      {saving ? "Enregistrement…" : "✓ Ajouter"}
                    </button>
                    <button onClick={()=>setShowAdd(false)}
                      style={{ padding:"10px 18px", borderRadius:10, border:`1px solid ${T.border}`, background:"transparent", color:T.muted, fontSize:13, cursor:"pointer" }}>
                      Annuler
                    </button>
                  </div>
                </div>
              ) : (
                <button onClick={()=>setShowAdd(true)}
                  style={{ width:"100%", padding:"11px", borderRadius:11, border:`1.5px dashed ${T.border}`, background:"transparent", color:T.accent, fontSize:13, fontWeight:700, cursor:"pointer", transition:"all .15s" }}
                  onMouseEnter={e=>e.currentTarget.style.background=T.accentDim}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  ＋ Ajouter un fournisseur à ce produit
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Modal fournisseur (CRUD) ──────────────────────────────────────
function FournisseurModal({ T, fournisseur=null, onClose, onSaved }) {
  const isEdit = !!fournisseur;
  const [form, setForm] = useState({
    nom: fournisseur?.nom||"", email: fournisseur?.email||"",
    telephone: fournisseur?.telephone||"", site_web: fournisseur?.site_web||"",
    notes: fournisseur?.notes||""
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");

  const inp = { background:T.bg, border:`1px solid ${T.border}`, borderRadius:9, padding:"10px 13px", color:T.text, fontSize:13, outline:"none", width:"100%", boxSizing:"border-box" };

  const save = async () => {
    if (!form.nom.trim()) { setError("Le nom est obligatoire"); return; }
    setSaving(true); setError("");
    try {
      const url    = isEdit ? `${BASE}/fournisseurs/${fournisseur.id}` : `${BASE}/fournisseurs`;
      const method = isEdit ? "PATCH" : "POST";
      const r   = await fetch(url, { method, headers:hdr(), body:JSON.stringify(form) });
      const res = await r.json().catch(() => ({ error: `HTTP ${r.status} — réponse invalide` }));
      if (!r.ok || res.error) { setError(res.error || `Erreur ${r.status}`); setSaving(false); return; }
      onSaved(res);
    } catch(e) { setError("Erreur réseau : " + e.message); }
    setSaving(false);
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(8,12,30,.4)", backdropFilter:"blur(6px)", zIndex:2100, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:20, width:"min(500px,100%)", padding:"28px 26px", boxShadow:"0 24px 64px rgba(0,0,0,.25)", animation:"nx-modal-open .28s cubic-bezier(.16,1,.3,1) both" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:22 }}>
          <div style={{ fontSize:16, fontWeight:900, color:T.text }}>{isEdit?"✏️ Modifier le fournisseur":"🏭 Nouveau fournisseur"}</div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:T.muted, cursor:"pointer", fontSize:20 }}>✕</button>
        </div>
        {error && <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:9, padding:"10px 14px", marginBottom:14, fontSize:13, color:"#dc2626" }}>⚠ {error}</div>}
        <div style={{ display:"flex", flexDirection:"column", gap:13 }}>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:".6px", display:"block", marginBottom:5 }}>Nom *</label>
            <input value={form.nom} onChange={e=>setForm(p=>({...p,nom:e.target.value}))} placeholder="Ex : Risco, Dahua, Ajax Systems…" style={inp}/>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <div>
              <label style={{ fontSize:11, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:".6px", display:"block", marginBottom:5 }}>Email</label>
              <input type="email" value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))} placeholder="commandes@fournisseur.fr" style={inp}/>
            </div>
            <div>
              <label style={{ fontSize:11, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:".6px", display:"block", marginBottom:5 }}>Téléphone</label>
              <input value={form.telephone} onChange={e=>setForm(p=>({...p,telephone:e.target.value}))} placeholder="03 XX XX XX XX" style={inp}/>
            </div>
          </div>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:".6px", display:"block", marginBottom:5 }}>Site web</label>
            <input value={form.site_web} onChange={e=>setForm(p=>({...p,site_web:e.target.value}))} placeholder="https://…" style={inp}/>
          </div>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:".6px", display:"block", marginBottom:5 }}>Notes</label>
            <textarea value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} rows={3} placeholder="Conditions, délais habituels, contact préféré…"
              style={{ ...inp, resize:"vertical", fontFamily:"inherit" }}/>
          </div>
        </div>
        <div style={{ display:"flex", gap:10, marginTop:20 }}>
          <button onClick={onClose} style={{ flex:1, padding:"11px", borderRadius:11, border:`1px solid ${T.border}`, background:"transparent", color:T.muted, fontSize:14, fontWeight:600, cursor:"pointer" }}>Annuler</button>
          <button onClick={save} disabled={saving}
            style={{ flex:2, padding:"11px", borderRadius:11, background:`linear-gradient(135deg,${T.accent},${T.purple||"#8b5cf6"})`, border:"none", color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer", opacity:saving?.7:1 }}>
            {saving ? "Enregistrement…" : isEdit ? "✓ Enregistrer" : "✓ Créer"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Onglet Fournisseurs ───────────────────────────────────────────
function FournisseursTab({ T }) {
  const [fournisseurs, setFournisseurs] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState("");
  const [modal, setModal]               = useState(null); // null | "new" | {fournisseur obj}

  const load = useCallback(async () => {
    setLoading(true);
    const data = await fetch(`${BASE}/fournisseurs`, { headers:hdr() }).then(r=>r.json()).catch(()=>[]);
    setFournisseurs(Array.isArray(data) ? data : []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const del = async (f) => {
    if (!window.confirm(`Supprimer "${f.nom}" ? Tous ses prix seront supprimés.`)) return;
    await fetch(`${BASE}/fournisseurs/${f.id}`, { method:"DELETE", headers:hdr() });
    load();
  };

  const filtered = useMemo(()=>{
    const q = search.toLowerCase();
    return fournisseurs.filter(f => !q || f.nom.toLowerCase().includes(q) || (f.email||"").toLowerCase().includes(q));
  }, [fournisseurs, search]);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      {modal && (
        <FournisseurModal T={T}
          fournisseur={modal === "new" ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}

      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10 }}>
        <div>
          <div style={{ fontSize:18, fontWeight:800, color:T.text }}>🏭 Fournisseurs</div>
          <div style={{ fontSize:13, color:T.muted, marginTop:3 }}>{fournisseurs.length} fournisseur{fournisseurs.length!==1?"s":""}</div>
        </div>
        <button onClick={() => setModal("new")}
          style={{ padding:"9px 18px", borderRadius:10, background:`linear-gradient(135deg,${T.accent},${T.purple||"#8b5cf6"})`, border:"none", color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer" }}>
          ＋ Nouveau fournisseur
        </button>
      </div>

      {/* Recherche */}
      <div style={{ display:"flex", alignItems:"center", gap:8, background:T.card, border:`1px solid ${T.border}`, borderRadius:11, padding:"9px 14px" }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Rechercher un fournisseur…"
          style={{ background:"none", border:"none", outline:"none", color:T.text, fontSize:13, flex:1 }}/>
        {search && <button onClick={()=>setSearch("")} style={{ background:"none", border:"none", color:"#94a3b8", cursor:"pointer", fontSize:14, lineHeight:1, padding:0 }}>✕</button>}
      </div>

      {loading ? (
        <div style={{ textAlign:"center", padding:40, color:T.muted }}>⏳ Chargement…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:"center", padding:"40px 20px", color:T.muted }}>
          <div style={{ fontSize:40, marginBottom:10 }}>🏭</div>
          <div style={{ fontSize:14 }}>Aucun fournisseur</div>
          <div style={{ fontSize:12, marginTop:4, opacity:.7 }}>Créez votre premier fournisseur pour commencer</div>
        </div>
      ) : (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))", gap:12 }}>
          {filtered.map(f => (
            <div key={f.id} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:16, padding:"18px 20px", display:"flex", flexDirection:"column", gap:10, transition:"box-shadow .15s" }}
              onMouseEnter={e=>e.currentTarget.style.boxShadow="0 4px 20px rgba(0,0,0,.08)"}
              onMouseLeave={e=>e.currentTarget.style.boxShadow="none"}>

              {/* Top */}
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:15, fontWeight:800, color:T.text, marginBottom:3 }}>{f.nom}</div>
                  {f.email && (
                    <a href={`mailto:${f.email}`} style={{ fontSize:12, color:T.accent, textDecoration:"none", display:"block", marginBottom:2 }}>
                      ✉️ {f.email}
                    </a>
                  )}
                  {f.telephone && <div style={{ fontSize:12, color:T.muted }}>📞 {f.telephone}</div>}
                  {f.site_web && (
                    <a href={f.site_web} target="_blank" rel="noreferrer" style={{ fontSize:11, color:T.accent, textDecoration:"none", display:"block", marginTop:2 }}>
                      🌐 {f.site_web.replace(/^https?:\/\//,"").split("/")[0]}
                    </a>
                  )}
                </div>
                <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                  <button onClick={() => setModal(f)}
                    style={{ width:30, height:30, borderRadius:8, border:`1px solid ${T.border}`, background:"transparent", color:T.muted, cursor:"pointer", fontSize:13, display:"flex", alignItems:"center", justifyContent:"center" }}>
                    ✏️
                  </button>
                  <button onClick={() => del(f)}
                    style={{ width:30, height:30, borderRadius:8, border:"1px solid #fecaca", background:"#fef2f2", color:"#dc2626", cursor:"pointer", fontSize:13, display:"flex", alignItems:"center", justifyContent:"center" }}>
                    🗑
                  </button>
                </div>
              </div>

              {/* Stats */}
              <div style={{ display:"flex", gap:10 }}>
                <div style={{ flex:1, background:T.bg, borderRadius:10, padding:"10px 12px", textAlign:"center" }}>
                  <div style={{ fontSize:18, fontWeight:900, color:T.text }}>{f.nb_produits||0}</div>
                  <div style={{ fontSize:10, color:T.muted, marginTop:2 }}>produit{f.nb_produits!==1?"s":""}</div>
                </div>
                {f.prix_min != null && (
                  <div style={{ flex:1, background:"#f0fdf4", borderRadius:10, padding:"10px 12px", textAlign:"center" }}>
                    <div style={{ fontSize:14, fontWeight:900, color:"#15803d" }}>{fmtEur(f.prix_min)}</div>
                    <div style={{ fontSize:10, color:"#15803d", marginTop:2, opacity:.7 }}>prix min</div>
                  </div>
                )}
                {f.prix_max != null && f.prix_max !== f.prix_min && (
                  <div style={{ flex:1, background:T.bg, borderRadius:10, padding:"10px 12px", textAlign:"center" }}>
                    <div style={{ fontSize:14, fontWeight:700, color:T.muted }}>{fmtEur(f.prix_max)}</div>
                    <div style={{ fontSize:10, color:T.muted, marginTop:2 }}>prix max</div>
                  </div>
                )}
              </div>

              {/* Notes */}
              {f.notes && (
                <div style={{ fontSize:11, color:T.muted, background:T.bg, borderRadius:8, padding:"8px 11px", lineHeight:1.5 }}>
                  {f.notes}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Composant principal StockView ─────────────────────────────────
export default function StockView({ T, stock = [], onUpdate, onDelete, onRefresh, onAdd, onResetAll, onDeleteAll }) {
  const [mainTab,  setMainTab]  = useState("produits"); // "produits" | "fournisseurs"
  const [search,   setSearch]   = useState("");
  const [catF,     setCatF]     = useState("tous");
  const [editing,  setEditing]  = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving,   setSaving]   = useState(false);
  const [prixModal, setPrixModal] = useState(null); // produit sélectionné

  const cats = useMemo(()=>["tous",...[...new Set(stock.map(x=>x.categorie||"Divers"))].sort()],[stock]);

  const filtered = useMemo(()=>{
    const q = search.toLowerCase();
    return stock.filter(it=>{
      const matchQ = !q ||
        (it.designation||it.nom||"").toLowerCase().includes(q) ||
        (it.reference||"").toLowerCase().includes(q) ||
        (it.marque||"").toLowerCase().includes(q);
      const matchC = catF==="tous" || (it.categorie||"Divers")===catF;
      return matchQ && matchC;
    });
  },[stock,search,catF]);

  // Analytics
  const outOfStock = stock.filter(p=>p.quantite===0).length;
  const critical   = stock.filter(p=>p.quantite>0&&p.quantite<=3).length;
  const topItems   = [...stock].sort((a,b)=>b.quantite-a.quantite).slice(0,3);

  const startEdit = (item) => {
    setEditing(item.id);
    setEditForm({ designation:item.designation||item.nom||"", reference:item.reference||"", categorie:item.categorie||"Divers", quantite:item.quantite||0, marque:item.marque||"" });
  };
  const saveEdit = async () => {
    setSaving(true);
    await onUpdate(editing, editForm);
    setSaving(false);
    setEditing(null);
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:18 }}>
      <style>{`
        .srow:hover { background:#f8fafc !important; }
        .srow:hover .sact { opacity:1 !important; transform:translateX(0) !important; }
        .sact { opacity:0; transform:translateX(6px); transition:opacity .18s,transform .18s; }
        .srow { transition:background .12s; }
      `}</style>

      {/* Modals */}
      {prixModal && <PrixModal T={T} produit={prixModal} onClose={()=>setPrixModal(null)}/>}

      {/* ── Entête + onglets principaux ── */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:12 }}>
        <div>
          <div style={{ fontSize:22, fontWeight:900, color:T.text, letterSpacing:"-0.6px" }}>Gestion du stock</div>
          <div style={{ fontSize:13, color:T.muted, marginTop:3 }}>{stock.length} produit{stock.length!==1?"s":""} · {[...new Set(stock.map(x=>x.categorie||"Divers"))].length} catégories</div>
        </div>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
          {mainTab==="produits" && <>
            <button onClick={onResetAll}
              style={{ padding:"9px 16px", borderRadius:10, border:`1px solid ${T.border}`, background:"transparent", color:T.muted, fontSize:12, fontWeight:700, cursor:"pointer" }}>
              🔄 Tout à 0
            </button>
            <button onClick={onDeleteAll}
              style={{ padding:"9px 16px", borderRadius:10, border:"1px solid #fecaca", background:"#fef2f2", color:"#dc2626", fontSize:12, fontWeight:700, cursor:"pointer" }}>
              🗑 Tout supprimer
            </button>
            <button onClick={onAdd}
              style={{ padding:"9px 18px", borderRadius:10, background:`linear-gradient(135deg,${T.accent},${T.purple||"#8b5cf6"})`, border:"none", color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer" }}>
              ＋ Produit
            </button>
          </>}
        </div>
      </div>

      {/* Onglets */}
      <div style={{ display:"flex", background:T.bg, border:`1px solid ${T.border}`, borderRadius:11, overflow:"hidden", alignSelf:"flex-start" }}>
        {[["produits","📦 Produits"],["fournisseurs","🏭 Fournisseurs"]].map(([k,l])=>(
          <button key={k} onClick={()=>setMainTab(k)}
            style={{ padding:"9px 20px", background:mainTab===k?T.accentDim:"transparent", border:"none", borderRight:`1px solid ${T.border}`, color:mainTab===k?T.accent:"#64748b", fontSize:13, fontWeight:mainTab===k?700:500, cursor:"pointer", transition:"all .15s" }}>
            {l}
          </button>
        ))}
      </div>

      {/* ════ ONGLET PRODUITS ════ */}
      {mainTab==="produits" && (
        <>
          {/* Mini-cards analytics */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12 }}>
            <div style={{ background:T.card, border:`1.5px solid ${outOfStock>0?"#fecaca":T.border}`, borderRadius:16, padding:"18px 20px", display:"flex", alignItems:"center", gap:16 }}>
              <div style={{ width:46, height:46, borderRadius:14, background:"#fef2f2", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, flexShrink:0 }}>🔴</div>
              <div>
                <div style={{ fontSize:28, fontWeight:900, color:outOfStock>0?"#dc2626":T.muted, lineHeight:1, letterSpacing:"-1px" }}>{outOfStock}</div>
                <div style={{ fontSize:12, color:T.muted, marginTop:4, fontWeight:600 }}>En rupture</div>
              </div>
            </div>
            <div style={{ background:T.card, border:`1.5px solid ${critical>0?"#fde68a":T.border}`, borderRadius:16, padding:"18px 20px", display:"flex", alignItems:"center", gap:16 }}>
              <div style={{ width:46, height:46, borderRadius:14, background:"#fffbeb", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, flexShrink:0 }}>🟡</div>
              <div>
                <div style={{ fontSize:28, fontWeight:900, color:critical>0?"#b45309":T.muted, lineHeight:1, letterSpacing:"-1px" }}>{critical}</div>
                <div style={{ fontSize:12, color:T.muted, marginTop:4, fontWeight:600 }}>Niveau critique (≤ 3)</div>
              </div>
            </div>
            <div style={{ background:T.card, border:`1.5px solid ${T.border}`, borderRadius:16, padding:"18px 20px", display:"flex", alignItems:"flex-start", gap:16 }}>
              <div style={{ width:46, height:46, borderRadius:14, background:"#f0fdf4", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, flexShrink:0 }}>✅</div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:12, fontWeight:700, color:T.text, marginBottom:6 }}>Top niveaux</div>
                {topItems.length===0
                  ? <div style={{ fontSize:12, color:T.muted, fontStyle:"italic" }}>—</div>
                  : topItems.map(p=>(
                    <div key={p.id} style={{ display:"flex", justifyContent:"space-between", gap:8, marginBottom:3 }}>
                      <span style={{ fontSize:11, color:"#64748b", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.designation||p.nom}</span>
                      <span style={{ fontSize:11, fontWeight:800, color:"#16a34a", flexShrink:0 }}>{p.quantite}</span>
                    </div>
                  ))
                }
              </div>
            </div>
          </div>

          {/* Tabs catégories */}
          <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
            {cats.map(c=>{
              const count = c==="tous" ? stock.length : stock.filter(x=>(x.categorie||"Divers")===c).length;
              const active = catF===c;
              return (
                <button key={c} onClick={()=>setCatF(c)}
                  style={{ padding:"7px 15px", borderRadius:20, border:`1.5px solid ${active?T.accent:T.border}`, background:active?T.accentDim:T.card, color:active?T.accent:"#64748b", fontSize:12, fontWeight:active?700:500, cursor:"pointer", transition:"all .15s", whiteSpace:"nowrap" }}>
                  {c==="tous"?"Tous":c}
                  <span style={{ marginLeft:5, fontSize:10, opacity:.65 }}>({count})</span>
                </button>
              );
            })}
          </div>

          {/* Barre recherche */}
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ flex:1, display:"flex", alignItems:"center", gap:8, background:T.card, border:`1px solid ${T.border}`, borderRadius:11, padding:"9px 14px" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Rechercher produit, référence, marque…"
                style={{ background:"none", border:"none", outline:"none", color:T.text, fontSize:13, flex:1 }}/>
              {search && <button onClick={()=>setSearch("")} style={{ background:"none", border:"none", color:"#94a3b8", cursor:"pointer", fontSize:14, lineHeight:1, padding:0 }}>✕</button>}
            </div>
            <span style={{ fontSize:12, color:T.muted, whiteSpace:"nowrap" }}>{filtered.length} produit{filtered.length!==1?"s":""}</span>
            {catF!=="tous" && (
              <button onClick={async()=>{
                if(!window.confirm(`Supprimer tous les "${catF}" ? (${filtered.length} produits)`)) return;
                await fetch(`${BASE}/stock/by-categorie/${encodeURIComponent(catF)}`,{method:"DELETE",headers:hdr()});
                onRefresh?.(); setCatF("tous");
              }} style={{ padding:"8px 14px", borderRadius:9, background:"#fef2f2", border:"1px solid #fecaca", color:"#dc2626", fontSize:12, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>
                🗑 Suppr. catégorie
              </button>
            )}
          </div>

          {/* Table */}
          <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:16, overflow:"hidden" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", minWidth:680 }}>
              <thead>
                <tr style={{ background:"#f8fafc", borderBottom:`1px solid ${T.border}` }}>
                  {["Produit","Marque","Catégorie","Quantité",""].map((col,i)=>(
                    <th key={i} style={{ padding:"12px 16px", textAlign:"left", fontSize:10, fontWeight:700, color:"#94a3b8", letterSpacing:".8px", textTransform:"uppercase", whiteSpace:"nowrap" }}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length===0 && (
                  <tr><td colSpan={5} style={{ padding:"48px 20px", textAlign:"center", color:"#94a3b8", fontSize:13 }}>
                    <div style={{ fontSize:32, marginBottom:10 }}>📦</div>Aucun produit trouvé
                  </td></tr>
                )}
                {filtered.map(item=>{
                  const isEdit = editing===item.id;
                  return (
                    <tr key={item.id} className="srow" style={{ borderBottom:`1px solid #f1f5f9`, background:isEdit?(T.accentDim||"#eff6ff"):"transparent" }}>
                      <td style={{ padding:"13px 16px", maxWidth:280 }}>
                        {isEdit
                          ? <input value={editForm.designation} onChange={e=>setEditForm(p=>({...p,designation:e.target.value}))}
                              style={{ background:T.bg, border:`1px solid ${T.border}`, borderRadius:8, padding:"8px 12px", color:T.text, fontSize:13, width:"100%", outline:"none" }}/>
                          : <div>
                              <div style={{ fontSize:13, fontWeight:700, color:"#1e293b", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{item.designation||item.nom}</div>
                              {item.reference && <div style={{ fontSize:10, color:"#94a3b8", fontFamily:"monospace", marginTop:2 }}>{item.reference}</div>}
                            </div>
                        }
                      </td>
                      <td style={{ padding:"13px 16px", fontSize:12, color:"#64748b" }}>
                        {isEdit
                          ? <input value={editForm.marque} onChange={e=>setEditForm(p=>({...p,marque:e.target.value}))}
                              style={{ background:T.bg, border:`1px solid ${T.border}`, borderRadius:8, padding:"8px 12px", color:T.text, fontSize:12, width:90, outline:"none" }}/>
                          : <span>{item.marque||"—"}</span>
                        }
                      </td>
                      <td style={{ padding:"13px 16px" }}>
                        {isEdit
                          ? <select value={editForm.categorie} onChange={e=>setEditForm(p=>({...p,categorie:e.target.value}))}
                              style={{ background:T.bg, border:`1px solid ${T.border}`, borderRadius:8, padding:"8px 12px", color:T.text, fontSize:12, outline:"none", cursor:"pointer" }}>
                              {CATS_STOCK.map(c=><option key={c}>{c}</option>)}
                            </select>
                          : <span style={{ fontSize:11, color:T.accent, background:T.accentDim, padding:"4px 11px", borderRadius:6, fontWeight:700, whiteSpace:"nowrap", display:"inline-block" }}>
                              {item.categorie||"Divers"}
                            </span>
                        }
                      </td>
                      <td style={{ padding:"13px 16px" }}>
                        {isEdit
                          ? <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                              <button onClick={()=>setEditForm(p=>({...p,quantite:Math.max(0,(p.quantite||0)-1)}))} style={{ width:30, height:30, borderRadius:8, border:`1px solid ${T.border}`, background:T.bg, color:T.text, cursor:"pointer", fontSize:18, display:"flex", alignItems:"center", justifyContent:"center" }}>−</button>
                              <input type="number" min="0" value={editForm.quantite} onChange={e=>setEditForm(p=>({...p,quantite:parseInt(e.target.value)||0}))} style={{ background:T.bg, border:`1px solid ${T.border}`, borderRadius:8, padding:"6px 8px", color:T.text, fontSize:15, fontWeight:800, width:64, textAlign:"center", outline:"none" }}/>
                              <button onClick={()=>setEditForm(p=>({...p,quantite:(p.quantite||0)+1}))} style={{ width:30, height:30, borderRadius:8, border:`1px solid ${T.border}`, background:T.bg, color:T.text, cursor:"pointer", fontSize:18, display:"flex", alignItems:"center", justifyContent:"center" }}>＋</button>
                            </div>
                          : <QtyBadge qty={item.quantite}/>
                        }
                      </td>
                      <td style={{ padding:"13px 16px", whiteSpace:"nowrap" }}>
                        {isEdit
                          ? <div style={{ display:"flex", gap:6 }}>
                              <button onClick={saveEdit} disabled={saving} style={{ padding:"7px 16px", borderRadius:9, background:T.accent, border:"none", color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer", opacity:saving?.6:1 }}>
                                {saving?"…":"✓ Sauver"}
                              </button>
                              <button onClick={()=>setEditing(null)} style={{ padding:"7px 12px", borderRadius:9, border:`1px solid ${T.border}`, background:"transparent", color:"#64748b", fontSize:12, cursor:"pointer" }}>
                                Annuler
                              </button>
                            </div>
                          : <div className="sact" style={{ display:"flex", gap:6 }}>
                              <button onClick={()=>setPrixModal(item)}
                                style={{ padding:"6px 12px", borderRadius:8, border:`1px solid ${T.border}`, background:"#fff", color:"#475569", fontSize:11, fontWeight:600, cursor:"pointer", whiteSpace:"nowrap" }}>
                                💰 Prix
                              </button>
                              <button onClick={()=>startEdit(item)}
                                style={{ padding:"6px 12px", borderRadius:8, border:`1px solid ${T.border}`, background:"#fff", color:"#475569", fontSize:11, fontWeight:600, cursor:"pointer", whiteSpace:"nowrap" }}>
                                ✏️ Ajuster
                              </button>
                              <button onClick={()=>window.open(`mailto:?subject=Commande - ${item.designation||item.nom}&body=Référence: ${item.reference||"—"}%0AMarque: ${item.marque||"—"}%0AQuantité souhaitée: `,"_blank")}
                                style={{ padding:"6px 12px", borderRadius:8, border:`1px solid ${T.border}`, background:"#fff", color:"#475569", fontSize:11, fontWeight:600, cursor:"pointer", whiteSpace:"nowrap" }}>
                                📦 Commander
                              </button>
                              <button onClick={async()=>{if(window.confirm("Supprimer ce produit ?"))await onDelete(item.id);}}
                                style={{ padding:"6px 10px", borderRadius:8, border:"1px solid #fecaca", background:"#fef2f2", color:"#dc2626", fontSize:11, cursor:"pointer" }}>
                                🗑
                              </button>
                            </div>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ════ ONGLET FOURNISSEURS ════ */}
      {mainTab==="fournisseurs" && <FournisseursTab T={T}/>}
    </div>
  );
}
